import React, { useContext, useEffect, useState } from 'react';
import {
    BrowserRouter as Router,
    Navigate,
    Route,
    Routes,
} from 'react-router-dom';
import './i18n';
import Home from './components/Home';
import Login from './components/login/Login';
import Register from './components/manage-user/Register';
import AuthContext, { StoredContext } from './store/auth-context';
import Navigation from './components/general-components/Navigation';
import AdminPage from './components/admin-page/AdminPage';
import RIGHTS_KEYS from './constants/rights-keys.js';
import styles from './App.module.css';
import Styles from './components/Styles';
import ResetPassword from './components/password-reset/ResetPassword';
import SignupPage from './components/sign-up/SignupPage';
import InitiatePasswordReset from './components/password-reset/InitiatePasswordReset';
import SearchHistory from './components/search/History';
import ResultsPage from './components/search/ResultsPage';
import BookmarksPage from './components/bookmarks/BookmarksPage';
import UserProfilePage from './components/user-page/UserProfilePage';
import SavedFilter from './components/custom-conditionals/SavedFilter';
import ImplantPage from './components/implant/ImplantPage';
import ImplantOverviewPage from './components/implant-overview/ImplantOverviewPage';
import VideoUploadPage from './components/video-upload-page/VideoUploadPage';
import VideoOverview from './components/video-overview/VideoOverview';
import ScrapingOverview from './components/scraping-overview/ScrapingOverview';
import RecentImplantsPage from './components/scraping-overview/RecentImplantsPage';
import ImplantExpiryPage from './components/implant-verification/ImplantVerificationPage';
import SourceFilePage from './components/scraping-overview/SourceFilePage';
import { ImplantProvider } from './store/implant-context';
import Disclaimer from './components/ui-components/Disclaimer';
import ConfirmEmailPage from './components/sign-up/ConfirmEmailPage';
import SearchIssue from './components/search-issues/SearchIssue';
import { FilterContextProvider } from './store/filter-context';
import PredefinedView from './components/custom-conditionals/view/PredefinedView';
import NewPredefinedConditionals from './components/custom-conditionals/NewPredefinedConditionals';
import EditPredefinedConditionals from './components/custom-conditionals/EditPredefinedConditionals';
import AllImplantsPage from './components/implant-list/AllImplantsPage';
import MetricsPage from './components/metrics/MetricsPage';
import ApprovalPage from './components/approval/ApprovalPage';
import { FolderDetailPage } from './components/group/FolderDetailPage';
import RoomOverview from './components/room-overview/RoomOverview';
import AddMRIMachine from './components/room-overview/AddMRIMachine';
import AddRoomPage from './components/room-overview/AddRoomPage';
import SignupSuccess from './components/sign-up/SignupSuccess';
import { SignupContextProvider } from './store/signup-context';
import UltimateGuidePage from './components/guide/UltimateGuidePage';
import FilesOverviewPage from './components/files/FilesOverviewPage';
import { Trans, useTranslation } from 'react-i18next';
import PERMISSION_KEYS from './constants/permission-keys';
import SyncVerificationPage from './components/active-sync/SyncVerificationPage';
import ActiveImplantsPage from './components/active-implants/ActiveImplantsPage';
import { EVENT_KEYS, trackLoad } from './services/api/trackingService';
import MissingImplantReports from './components/missing-implant-reports/MissingImplantReports';
import ManageNotificationsPage from './components/notifications/ManageNotificationsPage';
import {
    CaseWorkspacePage as ReportingWorkspacePage,
    ManageReports,
} from './components/reports';
import { Toaster } from './components/ui/toaster';
import GlobalApiErrorDialog from './components/general-components/GlobalApiErrorDialog';
import DepartmentSettingsPage from './components/department-variables/DepartmentSettingsPage';
import DepartmentPoliciesPage from './components/department-policies/DepartmentPoliciesPage';
import PolicyEditorPage from './components/department-policies/editor/PolicyEditorPage';
import PolicyReadPage from './components/department-policies/read/PolicyReadPage';
import PolicyTemplateEditor from './components/policy-templates/PolicyTemplateEditor';
import FacilitiesDashboard from './components/facilities/FacilitiesPage';
import FacilityDetail from './components/facilities/FacilityDetailPage';
import Workbook from './components/onboarding/Workbook';
import NoAccess from './components/onboarding/NoAccess';
import CPTAnalyticsPage from './components/reports/analytics/CPTAnalyticsPage';
import FeedbackSurveyPopupRunner from './components/feedback-surveys/FeedbackSurveyPopupRunner';

const App = () => {
    const context = useContext(AuthContext);
    let ctx: StoredContext = context.getContext();
    const [isNavOpen, setIsNavOpen] = useState(false);

    const { t, i18n } = useTranslation();

    useEffect(() => {
        if (ctx.isLoggedIn) {
            if (ctx.fontSize) {
                document.documentElement.style.fontSize = ctx.fontSize + 'px';
            }

            if (
                process.env.REACT_APP_ENV !== 'development' &&
                ctx.actor.role !== 'PremiumUser'
            ) {
                const script = document.createElement('script');

                script.src =
                    '//code.tidio.co/ay1gjvtsnztcwvotks3zsaj4ucti1bew.js';
                script.async = true;

                document.body.appendChild(script);
            }
        }
    }, []);

    // Lock document scrolling while the main authenticated app is mounted.
    // The app shell is a fixed 100vh layout that scrolls internally via .page,
    // so the document itself should never scroll. Third-party widgets injected
    // into <body> (e.g. the Tidio chat) can otherwise extend the document below
    // the shell and leave an empty area when scrolling. Scoped to the main app
    // so login/signup and pre-trial views keep normal scrolling.
    const isMainApp = ctx.isLoggedIn && !context.isPreTrial();

    // While the facility hasn't gone live yet (onboarding stage, mirrors
    // deriveFacilityStatus), site admins land on the facility overview
    // instead of the search home page.
    const isOnboardingFacility =
        Boolean(ctx.department) &&
        (!ctx.department.start_date ||
            new Date(ctx.department.start_date) > new Date());
    useEffect(() => {
        if (!isMainApp) return;
        document.documentElement.classList.add('appNoScroll');
        return () => {
            document.documentElement.classList.remove('appNoScroll');
        };
    }, [isMainApp]);

    const toggleMenu = () => {
        setIsNavOpen(!isNavOpen);
    };
    const closeMenu = () => {
        setIsNavOpen(false);
    };

    if (!ctx.isLoggedIn) {
        return (
            <React.Fragment>
                <div className={styles.app}>
                    <Router>
                        <SignupContextProvider>
                            <Routes>
                                <Route
                                    path="/reset/:id"
                                    element={<ResetPassword />}
                                />
                                <Route
                                    path="/initiate-reset"
                                    element={<InitiatePasswordReset />}
                                />
                                <Route
                                    path="/signup-success"
                                    element={<SignupSuccess />}
                                />
                                <Route
                                    path="/sign-up/:code"
                                    element={<SignupPage />}
                                />
                                <Route
                                    path="/sign-up/"
                                    element={<SignupPage />}
                                />
                                <Route
                                    path="/confirm-email"
                                    element={<ConfirmEmailPage />}
                                />
                                <Route path="/styles" element={<Styles />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/" element={<Login />} />
                                <Route
                                    path="*"
                                    element={<Navigate to="/" replace={true} />}
                                />
                                {/* TODO MATHIAS: Add catch all 404 page */}
                            </Routes>
                        </SignupContextProvider>
                    </Router>
                </div>
            </React.Fragment>
        );
    }

    if (context.isPreTrial()) {
        if (ctx.actor.role !== 'DeptAdmin') {
            return (
                <div data-theme={context.theme}>
                    <NoAccess />
                </div>
            );
        }
        return (
            <React.Fragment>
                <div data-theme={context.theme}>
                    <Router>
                        <Workbook />
                    </Router>
                    <Toaster />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <div className={styles.appWrapper}>
                {(ctx.actor.role === 'LiteUser' ||
                    ctx.expiryDate < new Date()) && (
                    <Disclaimer>
                        Your account has expired and therefore only has access
                        to limited features.{' '}
                        <a href="/user-profile">Manage your subscription</a> to
                        upgrade your account.
                    </Disclaimer>
                )}
                <div
                    className={`${styles.app} ${isNavOpen ? 'open' : ''}`}
                    data-theme={context.theme}
                >
                    <Router>
                        <FeedbackSurveyPopupRunner />
                        <Navigation
                            isOpen={isNavOpen}
                            toggleMenu={toggleMenu}
                        />

                        <div
                            id="page-container"
                            className={styles.page}
                            onClick={closeMenu}
                        >
                            <Routes>
                                <Route path="/styles" element={<Styles />} />
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.deptTools,
                                    ]) && (
                                        <Route
                                            path="/guide"
                                            element={<UltimateGuidePage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([RIGHTS_KEYS.deptTools]) &&
                                    (context.orRights([
                                        RIGHTS_KEYS.viewPolicies,
                                    ]) ||
                                        context.hasPermission(
                                            PERMISSION_KEYS.managePolicies
                                        )) && (
                                        <>
                                            <Route
                                                path="/department-policies"
                                                element={
                                                    <DepartmentPoliciesPage />
                                                }
                                            />
                                            <Route
                                                path="/department-policies/:id"
                                                element={<PolicyReadPage />}
                                            />
                                        </>
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageEmployees,
                                    ]) &&
                                    context.orRights([RIGHTS_KEYS.deptTools]) &&
                                    context.hasPermission(
                                        PERMISSION_KEYS.managePolicies
                                    ) && (
                                        <Route
                                            path="/department-policies/settings"
                                            element={<DepartmentSettingsPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([RIGHTS_KEYS.deptTools]) &&
                                    context.hasPermission(
                                        PERMISSION_KEYS.managePolicies
                                    ) && (
                                        <>
                                            <Route
                                                path="/department-policies/new"
                                                element={<PolicyEditorPage />}
                                            />
                                            <Route
                                                path="/department-policies/:id/edit"
                                                element={<PolicyEditorPage />}
                                            />
                                        </>
                                    )}
                                {ctx.isLoggedIn &&
                                    ctx.department &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageEmployees,
                                        RIGHTS_KEYS.manageDepartmentInfo,
                                        RIGHTS_KEYS.manageEndUsers,
                                    ]) && (
                                        <Route
                                            path="/facility"
                                            element={
                                                <FacilityDetail viewer="siteAdmin" />
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.detailedInfo,
                                    ]) && (
                                        <Route
                                            path="/video-overview"
                                            element={<VideoOverview />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllUsers,
                                        RIGHTS_KEYS.manageEmployees,
                                    ]) && (
                                        <Route
                                            path="/register"
                                            element={<Register />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageActors,
                                        RIGHTS_KEYS.manageAllImplants,
                                        RIGHTS_KEYS.manageDocumentSchemas,
                                        RIGHTS_KEYS.manageAllUsers,
                                        RIGHTS_KEYS.manageEndUsers,
                                    ]) && (
                                        <Route
                                            path="/admin"
                                            element={<AdminPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.dbSearch,
                                    ]) && (
                                        <Route
                                            path="/search/:term"
                                            element={
                                                <ImplantProvider>
                                                    <FilterContextProvider>
                                                        <ResultsPage />
                                                    </FilterContextProvider>
                                                </ImplantProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn && ctx.department && (
                                    <Route
                                        path="/room-overview"
                                        element={<RoomOverview />}
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <Route
                                            path="/sync-verification"
                                            element={<SyncVerificationPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    ctx.department &&
                                    context.orRights([
                                        RIGHTS_KEYS.advancedSearch,
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <>
                                            <Route
                                                path="/active-implants"
                                                element={<ActiveImplantsPage />}
                                            />
                                            <Route
                                                path="/active-implants/:id"
                                                element={<ActiveImplantsPage />}
                                            />
                                        </>
                                    )}
                                {ctx.isLoggedIn && ctx.department && (
                                    <Route
                                        path="/group/:id"
                                        element={<FolderDetailPage />}
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllUsers,
                                        RIGHTS_KEYS.manageEmployees,
                                    ]) && (
                                        <Route
                                            path="/register"
                                            element={<Register />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.dbSearch,
                                    ]) && (
                                        <Route
                                            path="/search/:term"
                                            element={
                                                <ImplantProvider>
                                                    <FilterContextProvider>
                                                        <ResultsPage />
                                                    </FilterContextProvider>
                                                </ImplantProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn && ctx.department && (
                                    <Route
                                        path="/room-overview"
                                        element={<RoomOverview />}
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    ctx.department &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageMRIOverview,
                                    ]) && (
                                        <Route
                                            path="/room-overview/create-room"
                                            element={<AddRoomPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    ctx.department &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageMRIOverview,
                                    ]) && (
                                        <Route
                                            path="/room-overview/create-room/:id"
                                            element={<AddMRIMachine />}
                                        />
                                    )}

                                {ctx.isLoggedIn && ctx.department && (
                                    <Route
                                        path="/group/:id"
                                        element={<FolderDetailPage />}
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.viewDepartmentFiles,
                                        RIGHTS_KEYS.manageAllImplants,
                                        RIGHTS_KEYS.manageAllUsers,
                                    ]) && (
                                        <Route
                                            path="/files"
                                            element={<FilesOverviewPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    ctx.department &&
                                    context.orRights([
                                        RIGHTS_KEYS.deptTools,
                                    ]) && (
                                        <Route
                                            path="/approved"
                                            element={<ApprovalPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.cptReporting,
                                    ]) && (
                                        <Route
                                            path="/cpt-report/manage"
                                            element={<ManageReports />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.cptReporting,
                                    ]) &&
                                    context.hasPermission(
                                        PERMISSION_KEYS.cptAnalytics
                                    ) && (
                                        <Route
                                            path="/cpt-analytics"
                                            element={<CPTAnalyticsPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.dbSearch,
                                        RIGHTS_KEYS.manageImplant,
                                    ]) && (
                                        <Route
                                            path="/implant/:id"
                                            element={
                                                <ImplantProvider>
                                                    <ImplantPage />
                                                </ImplantProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageImplant,
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <Route
                                            path="/implants/"
                                            element={
                                                <ImplantProvider>
                                                    <ImplantOverviewPage />
                                                </ImplantProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageImplant,
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <Route
                                            path="/searches/"
                                            element={<ScrapingOverview />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <Route
                                            path="/list-source-files/"
                                            element={<SourceFilePage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.verifyImplants,
                                    ]) && (
                                        <Route
                                            path="/recent-implants/"
                                            element={
                                                <ImplantProvider>
                                                    <RecentImplantsPage />
                                                </ImplantProvider>
                                            }
                                        />
                                    )}

                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.reverifyImplants,
                                    ]) && (
                                        <Route
                                            path="/reverification"
                                            element={
                                                <ImplantProvider>
                                                    <ImplantExpiryPage />
                                                </ImplantProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.dbSearch,
                                    ]) && (
                                        <Route
                                            path="/search"
                                            element={<Home />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <Route
                                            path="/search-issues"
                                            element={<SearchIssue />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllImplants,
                                    ]) && (
                                        <Route
                                            path="/missing-implant-reports"
                                            element={<MissingImplantReports />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.cptReporting,
                                    ]) && (
                                        <Route
                                            path="/reporting"
                                            element={<ReportingWorkspacePage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.cptReporting,
                                    ]) && (
                                        <Route
                                            path="/manage-reports"
                                            element={<ManageReports />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.cptReporting,
                                    ]) && (
                                        <Route
                                            path="/manage-reports/new"
                                            element={<ReportingWorkspacePage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.cptReporting,
                                    ]) && (
                                        <Route
                                            path="/manage-reports/:caseId"
                                            element={<ReportingWorkspacePage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.dbSearch,
                                    ]) && (
                                        <Route
                                            path="/history"
                                            element={<SearchHistory />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.advancedSearch,
                                    ]) && (
                                        <Route
                                            path="/bookmarks"
                                            element={<BookmarksPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.advancedSearch,
                                    ]) && (
                                        <Route
                                            path="/all"
                                            element={<AllImplantsPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.deptTools,
                                    ]) && (
                                        <Route
                                            path="/metrics"
                                            element={<MetricsPage />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageDepartments,
                                        RIGHTS_KEYS.manageAllUsers,
                                    ]) && (
                                        <>
                                            <Route
                                                path="/admin/facilities"
                                                element={
                                                    <FacilitiesDashboard />
                                                }
                                            />
                                            <Route
                                                path="/admin/facilities/:id"
                                                element={
                                                    <FacilityDetail viewer="niAdmin" />
                                                }
                                            />
                                        </>
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllUsers,
                                    ]) && (
                                        <Route
                                            path="/admin/policy-templates/new"
                                            element={<PolicyTemplateEditor />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageAllUsers,
                                    ]) && (
                                        <Route
                                            path="/admin/policy-templates/:id/edit"
                                            element={<PolicyTemplateEditor />}
                                        />
                                    )}
                                {ctx.isLoggedIn && (
                                    <Route
                                        path="/video-upload"
                                        element={<VideoUploadPage />}
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.manageNotifications,
                                    ]) && (
                                        <Route
                                            path="/manage-notifications"
                                            element={
                                                <ManageNotificationsPage />
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn && (
                                    <Route
                                        path="/user-profile"
                                        element={<UserProfilePage />}
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.dbSearch,
                                    ]) && (
                                        <Route
                                            path="/"
                                            element={
                                                isOnboardingFacility &&
                                                context.orRights([
                                                    RIGHTS_KEYS.manageEmployees,
                                                ]) ? (
                                                    <Navigate
                                                        to="/facility"
                                                        replace={true}
                                                    />
                                                ) : (
                                                    <Home />
                                                )
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn && (
                                    <Route
                                        path="*"
                                        element={
                                            <Navigate to="/" replace={true} />
                                        }
                                    />
                                )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.managePredefined,
                                    ]) && (
                                        <Route
                                            path="/predefined/new"
                                            element={
                                                <FilterContextProvider>
                                                    <NewPredefinedConditionals />
                                                </FilterContextProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.managePredefined,
                                    ]) && (
                                        <Route
                                            path="/predefined/edit/:id"
                                            element={
                                                <FilterContextProvider>
                                                    <EditPredefinedConditionals />
                                                </FilterContextProvider>
                                            }
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.managePredefined,
                                    ]) && (
                                        <Route
                                            path="/predefined/manage"
                                            element={<SavedFilter />}
                                        />
                                    )}
                                {ctx.isLoggedIn &&
                                    context.orRights([
                                        RIGHTS_KEYS.advancedSearch,
                                    ]) && (
                                        <Route
                                            path="/predefined/:id"
                                            element={
                                                <FilterContextProvider>
                                                    <PredefinedView />
                                                </FilterContextProvider>
                                            }
                                        />
                                    )}
                            </Routes>
                        </div>
                    </Router>
                    <Toaster />
                    <GlobalApiErrorDialog />
                </div>
            </div>
        </React.Fragment>
    );
};

export default App;
